import { useEffect, useRef, useState } from "react";

/**
 * Subscribe to realtime page events.
 *
 * Returns `{ presence }` — the live list of users currently viewing the page.
 *
 * onEvent receives raw events (block_updated, row_created, presence_join, etc.).
 *
 * Authenticates via first-message handshake.
 */
export default function usePageRealtime(pageId, onEvent) {
  const wsRef = useRef(null);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const [presence, setPresence] = useState([]);

  useEffect(() => {
    if (!pageId) { setPresence([]); return undefined; }
    const token = localStorage.getItem("tt_token");
    if (!token) return undefined;

    const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const wsBase = base.replace(/^http/i, "ws");
    const url = `${wsBase}/api/ws/pages/${pageId}`;

    let stopped = false;
    let backoff = 800;
    let pingTimer = null;

    const open = () => {
      if (stopped) return;
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => {
        backoff = 800;
        try { ws.send(JSON.stringify({ type: "auth", token })); } catch { /* socket may have closed */ }
        pingTimer = setInterval(() => {
          if (ws.readyState === 1) ws.send("ping");
        }, 25000);
      };
      ws.onmessage = (e) => {
        if (!e.data) return;
        if (e.data === "pong") return;
        try {
          const msg = JSON.parse(e.data);
          // Capture presence snapshots whenever the server sends them.
          if (Array.isArray(msg.presence)) {
            setPresence(msg.presence);
          }
          onEventRef.current?.(msg);
        } catch { /* swallow */ }
      };
      ws.onclose = () => {
        if (pingTimer) clearInterval(pingTimer);
        pingTimer = null;
        setPresence([]);
        if (stopped) return;
        setTimeout(open, backoff);
        backoff = Math.min(backoff * 2, 10000);
      };
      ws.onerror = () => { /* socket will close, handled above */ };
    };

    open();
    return () => {
      stopped = true;
      if (pingTimer) clearInterval(pingTimer);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch { /* noop */ }
        wsRef.current = null;
      }
    };
  }, [pageId]);

  return { presence };
}
